"use client";

import { useState, useEffect, useCallback } from "react";
import { Gift, Mail, Package, Loader2, CheckCircle, Search, Copy } from "lucide-react";
import { useTranslations } from "next-intl";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import CartDrawer from "@/components/cart/CartDrawer";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { SquareCardForm } from "@/components/checkout/SquareCardForm";
import { cn, formatPrice } from "@/lib/utils";
import { useToast } from "@/context/ToastContext";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { trackEvent } from "@/lib/track";

export default function GiftCardsPage() {
  const { toast } = useToast();
  const t = useTranslations("giftCard");
  const { giftCardPresets } = useSiteConfig();
  // Fall back to a sensible default if the config fetch hasn't
  // resolved yet so the preset picker isn't empty on first paint.
  const PRESET_AMOUNTS =
    giftCardPresets && giftCardPresets.length > 0
      ? giftCardPresets
      : [25, 50, 100, 250, 500];

  // Purchase form
  const [type, setType] = useState<"digital" | "physical">("digital");
  const [amount, setAmount] = useState<number | null>(100);
  const [customAmount, setCustomAmount] = useState("");
  const [isCustom, setIsCustom] = useState(false);
  const [purchaserName, setPurchaserName] = useState("");
  const [purchaserEmail, setPurchaserEmail] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [message, setMessage] = useState("");
  // Physical card shipping address
  const [shipAddress1, setShipAddress1] = useState("");
  const [shipAddress2, setShipAddress2] = useState("");
  const [shipCity, setShipCity] = useState("");
  const [shipState, setShipState] = useState("");
  const [shipZip, setShipZip] = useState("");
  const [purchasing, setPurchasing] = useState(false);
  const [purchased, setPurchased] = useState(false);
  const [purchasedCode, setPurchasedCode] = useState("");
  const [purchasedEmail, setPurchasedEmail] = useState("");
  // Two-step form: user fills details, hits Continue → card form appears.
  // We don't POST to /api/gift-cards until Square returns a sourceId so
  // we never create an unpaid gift card row.
  const [showPayment, setShowPayment] = useState(false);

  // Flip card
  const [flipped, setFlipped] = useState(false);

  // Balance check
  const [checkCode, setCheckCode] = useState("");
  const [checking, setChecking] = useState(false);
  const [balanceResult, setBalanceResult] = useState<{
    valid: boolean;
    balance: number;
  } | null>(null);

  // Confetti effect
  const triggerConfetti = useCallback(() => {
    const colors = [
      "#A58D76", // brand-gold
      "#C4AD96", // brand-gold-light
      "#D4B896", // gold variation
      "#BFA37A", // gold variation
      "#FFFFFF", // white
      "#FAF9F7", // cream
      "#E8D5C0", // warm gold
    ];
    const container = document.createElement("div");
    container.setAttribute("aria-hidden", "true");
    document.body.appendChild(container);

    const pieceCount = 60 + Math.floor(Math.random() * 20); // 60-80 pieces
    for (let i = 0; i < pieceCount; i++) {
      const piece = document.createElement("div");
      piece.className = "confetti-piece";
      piece.style.left = `${Math.random() * 100}vw`;
      piece.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
      piece.style.animationDelay = `${Math.random() * 1.5}s`;
      piece.style.animationDuration = `${2 + Math.random() * 2}s`;
      piece.style.borderRadius = Math.random() > 0.5 ? "50%" : "0";
      piece.style.width = `${6 + Math.random() * 8}px`;
      piece.style.height = `${6 + Math.random() * 8}px`;
      container.appendChild(piece);
    }

    // Cleanup after animations complete
    setTimeout(() => {
      container.remove();
    }, 5000);
  }, []);

  // Trigger confetti when purchase succeeds
  useEffect(() => {
    if (purchased) {
      triggerConfetti();
      setFlipped(false); // Reset flip state for new purchase
    }
  }, [purchased, triggerConfetti]);

  const selectedAmount = isCustom
    ? parseFloat(customAmount) || 0
    : amount || 0;

  const handleSelectAmount = (amt: number) => {
    setAmount(amt);
    setIsCustom(false);
    setCustomAmount("");
    trackEvent("gift_card_amount_selected", { amount: amt, isCustom: false });
  };

  const handleCustomAmount = () => {
    setIsCustom(true);
    setAmount(null);
    trackEvent("gift_card_amount_selected", { amount: 0, isCustom: true });
  };

  // Step 1 — validate fields and reveal SquareCardForm. No network call
  // yet; we wait for the customer to tokenize a card.
  const handleContinueToPayment = (e: React.FormEvent) => {
    e.preventDefault();

    if (!purchaserName || !purchaserEmail) {
      toast(t("fillNameEmail"), "error");
      return;
    }

    if (selectedAmount < 25 || selectedAmount > 1000) {
      toast(t("amountRange"), "error");
      return;
    }

    if (type === "digital" && !recipientEmail && !purchaserEmail) {
      toast(t("emailRequiredDigital"), "error");
      return;
    }

    if (type === "physical" && (!shipAddress1 || !shipCity || !shipState || !shipZip)) {
      toast(t("shippingRequired"), "error");
      return;
    }

    setShowPayment(true);
    trackEvent("gift_card_continue_to_payment", { type, amount: selectedAmount });
  };

  // Step 2 — Square returned a sourceId; complete the purchase.
  const handlePaymentSuccess = async (sourceId: string) => {
    setPurchasing(true);
    try {
      const res = await fetch("/api/gift-cards", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type,
          amount: selectedAmount,
          purchaserName,
          purchaserEmail,
          recipientName: recipientName || null,
          recipientEmail: recipientEmail || null,
          message: message || null,
          sourceId,
          ...(type === "physical" && {
            recipientAddress: `${recipientName || purchaserName}\n${shipAddress1}${shipAddress2 ? `\n${shipAddress2}` : ""}\n${shipCity}, ${shipState} ${shipZip}`,
          }),
        }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Failed to purchase gift card");
      }

      const data = await res.json();
      setPurchased(true);
      setPurchasedCode(data.giftCard.code);
      setPurchasedEmail(recipientEmail || purchaserEmail);
      trackEvent("gift_card_purchase_submitted", { type, amount: selectedAmount });
      toast(t("purchased"), "success");
    } catch (err) {
      toast(
        err instanceof Error ? err.message : "Failed to purchase gift card",
        "error"
      );
      setPurchasing(false); // allow retry without re-tokenizing on recoverable errors
    }
  };

  const handleCheckBalance = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!checkCode) return;

    setChecking(true);
    setBalanceResult(null);
    try {
      const res = await fetch(
        `/api/gift-cards/check?code=${encodeURIComponent(checkCode)}`
      );
      const data = await res.json();
      trackEvent("gift_card_balance_checked");
      setBalanceResult(data);
    } catch {
      toast(t("failedCheckBalance"), "error");
    } finally {
      setChecking(false);
    }
  };

  return (
    <>
      <Header />
      <CartDrawer />
      <main className="min-h-screen">
        {/* Hero */}
        <section className="bg-brand-cream/50 py-16 sm:py-24">
          <div className="container-luxury text-center">
            <div className="flex items-center justify-center mb-6">
              <div className="flex items-center justify-center w-16 h-16 rounded-full bg-brand-gold/10">
                <Gift className="h-8 w-8 text-brand-gold" />
              </div>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl text-brand-dark mb-4">
              {t("title")}
            </h1>
            <p className="text-sm text-brand-muted max-w-lg mx-auto leading-relaxed">
              {t("subtitle")}
            </p>
          </div>
        </section>

        {/* How It Works */}
        {!purchased && (
          <section className="container-luxury py-12 sm:py-16">
            <h2 className="font-display text-2xl text-brand-dark text-center mb-10">
              {t("howItWorksTitle")}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-3xl mx-auto">
              {[
                { step: "1", title: t("howItWorksStep1Title"), desc: t("howItWorksStep1Desc") },
                { step: "2", title: t("howItWorksStep2Title"), desc: t("howItWorksStep2Desc") },
                { step: "3", title: t("howItWorksStep3Title"), desc: t("howItWorksStep3Desc") },
              ].map((item) => (
                <div key={item.step} className="text-center">
                  <div className="mx-auto mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-brand-gold/10 text-sm font-medium text-brand-gold">
                    {item.step}
                  </div>
                  <h3 className="font-display text-base text-brand-dark mb-2">{item.title}</h3>
                  <p className="text-xs text-brand-muted leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
            <p className="text-center text-sm text-brand-muted leading-relaxed max-w-xl mx-auto mt-10 italic">
              {t("valueProposition")}
            </p>
          </section>
        )}

        {purchased ? (
          /* Success State with Flip Card + Confetti */
          <section className="container-luxury py-16 sm:py-20">
            <div className="max-w-lg mx-auto text-center">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-6 animate-bounce-in" />
              <h2 className="font-display text-3xl text-brand-dark mb-4">
                {t("purchased")}
              </h2>
              <p className="text-sm text-brand-muted mb-8">
                {t("purchasedMessage", { email: purchasedEmail })}
              </p>
              {type === "digital" && (
                <div
                  className="animate-bounce-in mb-8"
                  style={{ perspective: "800px" }}
                >
                  <div className={cn("flip-card-inner relative", flipped && "flipped")}>
                    {/* Front of card */}
                    <div className="flip-card-front">
                      <div className="border-2 border-brand-gold rounded-lg bg-brand-cream p-10 sm:p-12">
                        <div className="flex items-center justify-center mb-6">
                          <div className="flex items-center justify-center w-20 h-20 rounded-full bg-brand-gold/10">
                            <Gift className="h-10 w-10 text-brand-gold" />
                          </div>
                        </div>
                        <h3 className="font-display text-2xl text-brand-dark mb-3">
                          {t("giftCardReady")}
                        </h3>
                        <p className="text-brand-gold text-xl font-bold mb-6">
                          {formatPrice(selectedAmount)}
                        </p>
                        <Button
                          variant="gold"
                          onClick={() => setFlipped(true)}
                        >
                          {t("revealCode")}
                        </Button>
                        <p className="text-xs text-brand-muted mt-4">
                          {t("tapToReveal")}
                        </p>
                      </div>
                    </div>

                    {/* Back of card */}
                    <div className="flip-card-back absolute inset-0">
                      <div className="bg-brand-dark rounded-lg p-8 sm:p-10 h-full flex flex-col items-center justify-center border-2 border-brand-gold">
                        <p className="text-brand-gold text-xs uppercase tracking-[0.2em] mb-3">
                          {t("giftCardCode")}
                        </p>
                        <p className="text-white text-2xl sm:text-3xl font-mono font-bold tracking-widest mb-3">
                          {purchasedCode}
                        </p>
                        <p className="text-brand-gold text-xl font-bold mb-6">
                          {formatPrice(selectedAmount)}
                        </p>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(purchasedCode);
                            toast(t("codeCopied"), "success");
                          }}
                          className="flex items-center gap-2 px-4 py-2 text-sm text-white/80 hover:text-white border border-white/20 hover:border-white/40 rounded transition-all duration-200"
                        >
                          <Copy className="h-4 w-4" />
                          {t("copyCode")}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <Button
                variant="gold"
                onClick={() => {
                  setPurchased(false);
                  setShowPayment(false);
                  setFlipped(false);
                  setPurchaserName("");
                  setPurchaserEmail("");
                  setRecipientName("");
                  setRecipientEmail("");
                  setMessage("");
                  setAmount(100);
                  setIsCustom(false);
                  setCustomAmount("");
                }}
              >
                {t("purchaseAnother")}
              </Button>
            </div>
          </section>
        ) : (
          /* Purchase Form */
          <section className="container-luxury py-16 sm:py-20">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              <div className="lg:col-span-2">
                <form onSubmit={handleContinueToPayment} className="space-y-10">
                  {/* Card Type */}
                  <div>
                    <h2 className="font-display text-xl text-brand-dark mb-6">
                      {t("chooseTypePlaceholder")}
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <button
                        type="button"
                        onClick={() => { setType("digital"); trackEvent("gift_card_type_selected", { type: "digital" }); }}
                        className={cn(
                          "text-start p-6 border transition-all duration-300",
                          type === "digital"
                            ? "border-brand-gold bg-brand-cream/30 shadow-sm"
                            : "border-gray-100 hover:border-gray-200"
                        )}
                      >
                        <div
                          className={cn(
                            "flex items-center justify-center w-12 h-12 rounded-full mb-4 transition-colors",
                            type === "digital"
                              ? "bg-brand-gold text-white"
                              : "bg-brand-cream text-brand-gold"
                          )}
                        >
                          <Mail className="h-5 w-5" />
                        </div>
                        <h3 className="font-display text-lg text-brand-dark mb-1">
                          {t("digital")}
                        </h3>
                        <p className="text-xs text-brand-muted">
                          {t("digitalDesc")}
                        </p>
                      </button>
                      <button
                        type="button"
                        onClick={() => { setType("physical"); trackEvent("gift_card_type_selected", { type: "physical" }); }}
                        className={cn(
                          "text-start p-6 border transition-all duration-300",
                          type === "physical"
                            ? "border-brand-gold bg-brand-cream/30 shadow-sm"
                            : "border-gray-100 hover:border-gray-200"
                        )}
                      >
                        <div
                          className={cn(
                            "flex items-center justify-center w-12 h-12 rounded-full mb-4 transition-colors",
                            type === "physical"
                              ? "bg-brand-gold text-white"
                              : "bg-brand-cream text-brand-gold"
                          )}
                        >
                          <Package className="h-5 w-5" />
                        </div>
                        <h3 className="font-display text-lg text-brand-dark mb-1">
                          {t("physical")}
                        </h3>
                        <p className="text-xs text-brand-muted">
                          {t("physicalDesc")}
                        </p>
                      </button>
                    </div>
                  </div>

                  {/* Amount */}
                  <div>
                    <h2 className="font-display text-xl text-brand-dark mb-6">
                      {t("selectAmount")}
                    </h2>
                    <div className="flex flex-wrap gap-3 mb-4">
                      {PRESET_AMOUNTS.map((amt) => (
                        <button
                          key={amt}
                          type="button"
                          onClick={() => handleSelectAmount(amt)}
                          className={cn(
                            "px-6 py-3 text-sm font-medium border transition-all duration-300",
                            !isCustom && amount === amt
                              ? "border-brand-gold bg-brand-gold text-white"
                              : "border-gray-200 text-brand-dark hover:border-brand-gold"
                          )}
                        >
                          {formatPrice(amt)}
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={handleCustomAmount}
                        className={cn(
                          "px-6 py-3 text-sm font-medium border transition-all duration-300",
                          isCustom
                            ? "border-brand-gold bg-brand-gold text-white"
                            : "border-gray-200 text-brand-dark hover:border-brand-gold"
                        )}
                      >
                        {t("customAmount")}
                      </button>
                    </div>
                    {isCustom && (
                      <div className="max-w-xs">
                        <Input
                          type="number"
                          min="25"
                          max="1000"
                          value={customAmount}
                          onChange={(e) => setCustomAmount(e.target.value)}
                          placeholder={t("customAmountPlaceholder")}
                        />
                      </div>
                    )}
                  </div>

                  {/* Your Info */}
                  <div>
                    <h2 className="font-display text-xl text-brand-dark mb-6">
                      {t("yourInfo")}
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <Input
                        label={t("yourName")}
                        placeholder={t("namePlaceholder")}
                        value={purchaserName}
                        onChange={(e) => setPurchaserName(e.target.value)}
                      />
                      <Input
                        label={t("yourEmail")}
                        type="email"
                        placeholder={t("emailPlaceholder")}
                        value={purchaserEmail}
                        onChange={(e) => setPurchaserEmail(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Recipient Info */}
                  <div>
                    <h2 className="font-display text-xl text-brand-dark mb-6">
                      {t("recipientInfo")}
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <Input
                        label={t("recipientName")}
                        placeholder={t("recipientNamePlaceholder")}
                        value={recipientName}
                        onChange={(e) => setRecipientName(e.target.value)}
                      />
                      <Input
                        label={t("recipientEmail")}
                        type="email"
                        placeholder={t("recipientEmailPlaceholder")}
                        value={recipientEmail}
                        onChange={(e) => setRecipientEmail(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Shipping Address (physical only) */}
                  {type === "physical" && (
                    <div>
                      <h2 className="font-display text-xl text-brand-dark mb-2">
                        {t("shippingAddress")}
                      </h2>
                      <p className="text-xs text-brand-muted mb-6">{t("shippingAddressDesc")}</p>
                      <div className="space-y-4">
                        <Input
                          label={t("addressLine1")}
                          placeholder={t("placeholderStreet")}
                          value={shipAddress1}
                          onChange={(e) => setShipAddress1(e.target.value)}
                          required
                        />
                        <Input
                          label={t("addressLine2")}
                          placeholder={t("placeholderApt")}
                          value={shipAddress2}
                          onChange={(e) => setShipAddress2(e.target.value)}
                        />
                        <div className="grid grid-cols-3 gap-4">
                          <Input
                            label={t("city")}
                            placeholder={t("placeholderCity")}
                            value={shipCity}
                            onChange={(e) => setShipCity(e.target.value)}
                            required
                          />
                          <Input
                            label={t("state")}
                            placeholder={t("placeholderState")}
                            value={shipState}
                            onChange={(e) => setShipState(e.target.value)}
                            required
                          />
                          <Input
                            label={t("zip")}
                            placeholder={t("placeholderZip")}
                            value={shipZip}
                            onChange={(e) => setShipZip(e.target.value)}
                            required
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Personal Message */}
                  <div>
                    <h2 className="font-display text-xl text-brand-dark mb-6">
                      {t("personalMessage")}
                    </h2>
                    <div className="w-full">
                      <textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder={t("messagePlaceholder")}
                        rows={4}
                        className="w-full border-0 border-b border-brand-dark/20 bg-transparent px-0 py-2.5 text-sm text-brand-dark placeholder:text-brand-muted/50 focus:border-brand-gold focus:outline-none focus:ring-0 resize-none"
                      />
                    </div>
                  </div>

                  {/* Submit / Payment */}
                  {!showPayment ? (
                    <Button
                      type="submit"
                      variant="gold"
                      size="lg"
                      disabled={
                        selectedAmount < 25 ||
                        selectedAmount > 1000
                      }
                    >
                      Continue to payment —{" "}
                      {selectedAmount >= 25
                        ? formatPrice(selectedAmount)
                        : "$0"}
                    </Button>
                  ) : (
                    <div className="rounded-lg border border-brand-gold/20 bg-brand-cream/30 p-5 sm:p-6 space-y-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-display text-base text-brand-dark">
                            Payment
                          </p>
                          <p className="text-xs text-brand-muted mt-0.5">
                            {type === "digital" ? "Digital" : "Physical"} gift card — {formatPrice(selectedAmount)}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowPayment(false)}
                          disabled={purchasing}
                          className="text-xs text-brand-muted hover:text-brand-dark underline disabled:opacity-50"
                        >
                          Edit details
                        </button>
                      </div>
                      <SquareCardForm
                        amount={selectedAmount}
                        isProcessing={purchasing}
                        setIsProcessing={setPurchasing}
                        onPaymentSuccess={handlePaymentSuccess}
                        onPaymentError={(msg) => toast(msg, "error")}
                        buttonLabel={`Pay ${formatPrice(selectedAmount)}`}
                        processingLabel={t("purchasing")}
                        walletLabel="Rivky Wigs Gift Card"
                      />
                    </div>
                  )}
                </form>
              </div>

              {/* Sidebar: Check Balance */}
              <div className="lg:col-span-1">
                <div className="bg-brand-cream/50 border border-gray-100 p-5 sm:p-8 sticky top-24">
                  <h3 className="font-display text-lg text-brand-dark mb-6">
                    {t("checkBalance")}
                  </h3>
                  <form onSubmit={handleCheckBalance} className="space-y-4">
                    <Input
                      placeholder={t("enterCode")}
                      value={checkCode}
                      onChange={(e) => {
                        setCheckCode(e.target.value.toUpperCase());
                        setBalanceResult(null);
                      }}
                    />
                    <Button
                      type="submit"
                      variant="secondary"
                      size="md"
                      loading={checking}
                      className="w-full"
                    >
                      {checking ? (
                        <span className="flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Checking...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <Search className="h-4 w-4" />
                          {t("check")}
                        </span>
                      )}
                    </Button>
                  </form>

                  {balanceResult && (
                    <div className="mt-6 pt-6 border-t border-gray-200">
                      {balanceResult.valid ? (
                        <div className="text-center">
                          <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
                          <p className="text-sm font-medium text-brand-dark">
                            {t("balance", {
                              amount: formatPrice(balanceResult.balance),
                            })}
                          </p>
                        </div>
                      ) : (
                        <div className="text-center">
                          <p className="text-sm text-red-600">
                            {t("invalidCode")}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Info Box */}
                  <div className="border-t border-gray-200 mt-6 pt-6">
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <Mail className="h-4 w-4 text-brand-gold mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-xs font-medium text-brand-dark">
                            {t("digital")}
                          </p>
                          <p className="text-xs text-brand-muted">
                            {t("digitalDesc")}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <Package className="h-4 w-4 text-brand-gold mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-xs font-medium text-brand-dark">
                            {t("physical")}
                          </p>
                          <p className="text-xs text-brand-muted">
                            {t("physicalDesc")}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}
